import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { CreateBaby } from "./routes/CreateBaby";
import { BabyShell } from "./routes/BabyShell";
import { BabyPicker } from "./routes/BabyPicker";
import { Home } from "./routes/Home";
import { History } from "./routes/History";
import { Settings } from "./routes/Settings";
import { Login } from "./routes/Login";
import { Join } from "./routes/Join";
import { RequireAuth } from "./routes/RequireAuth";
import { UpdatePrompt } from "./components/UpdatePrompt";
import { lastUsedBabyId } from "./lib/babyId";
import { useAuth } from "./lib/AuthContext";
import { chooseLanding, listMyBabies } from "./lib/myBabies";
import { useT } from "./lib/LocaleContext";

function Landing() {
  const t = useT();
  const { user } = useAuth();
  // `null` = still deciding; "create" = no baby on this account;
  // "pick" = more than one and no idea which; else a baby id.
  const [dest, setDest] = useState<string | "create" | "pick" | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      const babies = await listMyBabies(user.id);
      if (cancelled) return;
      // The pointer is trusted only when it still names a baby this account can
      // open — storage the PWA wiped, or a pointer left by another account, must
      // not decide anything. Two babies and no pointer means we ask.
      const choice = chooseLanding(babies, lastUsedBabyId.get(user.id));
      setDest(choice.kind === "baby" ? choice.id : choice.kind);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  if (dest === null) {
    return (
      <main className="screen screen--center">
        <p className="muted">{t.common.loading}</p>
      </main>
    );
  }
  if (dest === "create") return <CreateBaby />;
  if (dest === "pick") return <Navigate to="/babies" replace />;
  return <Navigate to={`/b/${dest}`} replace />;
}

export function App() {
  return (
    <>
      <Routes>
        {/* Public — these mint their own session. */}
        <Route path="/login" element={<Login />} />
        <Route path="/join/:token" element={<Join />} />

        {/* Signed-in. */}
        <Route path="/" element={<RequireAuth><Landing /></RequireAuth>} />
        <Route path="/new" element={<RequireAuth><CreateBaby /></RequireAuth>} />
        <Route path="/babies" element={<RequireAuth><BabyPicker /></RequireAuth>} />
        <Route path="/b/:id" element={<RequireAuth><BabyShell /></RequireAuth>}>
          <Route index element={<Home />} />
          <Route path="history" element={<History />} />
          <Route path="settings" element={<Settings />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <UpdatePrompt />
    </>
  );
}
